"use client";

import React from "react";
import { motion } from "framer-motion";
import { 
  CheckCircle, 
  Globe, 
  Shield, 
  Zap,
  TrendingUp,
  Clock,
  Users,
  Star,
  ShoppingCart,
  Headphones,
  Newspaper,
  BookOpen,
  HelpCircle,
  MessageCircle,
  FileText,
  Settings,
  Truck,
  Home,
  Wrench,
  LucideIcon
} from "lucide-react";

interface FeatureItem {
  icon?: React.ReactNode | string;
  title: string;
  description: string;
}

interface FeatureGridProps {
  features: FeatureItem[];
  className?: string;
  /** Number of columns: 2, 3, or 4 */
  cols?: 2 | 3 | 4;
  /** Show accent bars on left edge of each column */
  showAccent?: boolean;
}

// Icon mapping for string-based icons
const iconMap: Record<string, LucideIcon> = {
  check: CheckCircle,
  globe: Globe,
  shield: Shield,
  zap: Zap,
  trending: TrendingUp,
  clock: Clock,
  users: Users,
  star: Star,
  "shopping-cart": ShoppingCart,
  headphones: Headphones,
  newspaper: Newspaper,
  "book-open": BookOpen,
  "help-circle": HelpCircle,
  "message-circle": MessageCircle,
  "file-text": FileText,
  settings: Settings,
  truck: Truck,
  home: Home,
  wrench: Wrench,
};

function getIcon(icon: React.ReactNode | string | undefined): React.ReactNode {
  if (!icon) return <CheckCircle className="w-full h-full" />;
  if (typeof icon === "string") {
    const IconComponent = iconMap[icon] || CheckCircle;
    return <IconComponent className="w-full h-full" />;
  }
  return icon;
}

/**
 * FeatureGrid Component
 * 
 * A Stripe-style feature grid with icons, titles, and descriptions.
 * Aligns with the 4-column grid system with vertical dividers.
 * 
 * Usage:
 * <FeatureGrid features={[
 *   { icon: "check", title: "Feature 1", description: "Description..." },
 *   { icon: "globe", title: "Feature 2", description: "Description..." },
 * ]} />
 */
export default function FeatureGrid({ 
  features, 
  className = "",
  cols = 4,
  showAccent = true
}: FeatureGridProps) {
  const colClasses = {
    2: "grid-cols-1 md:grid-cols-2",
    3: "grid-cols-1 md:grid-cols-2 lg:grid-cols-3",
    4: "grid-cols-1 md:grid-cols-2 lg:grid-cols-4",
  };

  return (
    <div className={`w-full ${className}`}>
      {/* Outer grid - no gap, columns are flush */}
      <div className={`grid ${colClasses[cols]} gap-6 lg:gap-0`}>
        {features.map((feature, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: index * 0.08 }}
            viewport={{ once: true }}
            className="relative"
          >
            {/* Accent bar on column boundary - only if showAccent is true */}
            {showAccent && (
              <div 
                className="absolute left-0 top-2 lg:top-0 w-0.5 h-8 rounded-full z-10"
                style={{ backgroundColor: 'var(--mininav-active-color, #14b8a6)' }}
              />
            )}
            
            {/* The column content with uniform padding */}
            <div className="px-4 lg:px-6 py-2 lg:py-0 mb-4 lg:mb-0">
              
              {/* Icon */}
              <div 
                className="w-9 h-9 sm:w-10 sm:h-10 mb-3 rounded-lg flex items-center justify-center text-white"
                style={{
                  backgroundColor: 'var(--mininav-active-color, #14b8a6)',
                }}
              >
                <div className="w-4 h-4 sm:w-5 sm:h-5">
                  {getIcon(feature.icon)}
                </div>
              </div>

              {/* Title */}
              <h2 className="text-base sm:text-lg font-satoshi font-semibold text-gray-800 mb-2">
                {feature.title}
              </h2>

              {/* Description */}
              <p className="text-sm sm:text-base font-light text-gray-600 leading-relaxed">
                {feature.description}
              </p>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

// Preset features for demos
export const sampleFeatures: FeatureItem[] = [
  {
    icon: "check",
    title: "Real time turnover control",
    description: "Track every property turnover in real time with live status updates, proof of completion, and portfolio wide visibility across all units."
  },
  {
    icon: "globe", 
    title: "Verified quality standards",
    description: "AI supervised quality checks and standards verification deliver consistent cleaning and linen outcomes at scale, with measurable reporting and instant exception alerts."
  },
  {
    icon: "shield",
    title: "Predictive stock replenishment",
    description: "Automated replenishment and ordering based on usage patterns prevents stockouts, reduces waste, and cuts emergency purchases across your portfolio."
  },
  {
    icon: "zap",
    title: "Fixed, predictable turnover costs",
    description: "All in turnover pricing with clear inclusions keeps costs predictable, reduces operational leakage, and protects margin as you scale nationwide."
  },
];
